import React, { createContext, useMemo } from 'react';
import { useSort } from 'new/elements/Table/useSort';
import { TableProps } from 'new/elements/Table/types';
import { useSelect } from 'new/elements/Table/useSelect';
import { useFilter, ColumnFilters } from 'new/elements/Table/useFilter';

export interface ITableContext<T> {
  selectedRows: T[];
  onSelectRow: (row: T) => void;
  onSelectAllRows: () => void;
  allRowsSelected: boolean;
  sortedRows: T[];
  filteredRows: T[];
  filters: ColumnFilters<T>;
  onFilterChange: (field: keyof T, value: any) => void;
  clearFilters: () => void;
  selectableRows: T[];
  sortDirection: 'none' | 'asc' | 'desc';
  sortField: keyof T;
  rows: T[];
  onClickSort: (field: keyof T) => void;
}

export const TableContext = createContext<ITableContext<any>>({
  selectedRows: [],
  onSelectRow: () => {},
  onSelectAllRows: () => {},
  allRowsSelected: false,
  rows: [],
  sortedRows: [],
  filteredRows: [],
  filters: {},
  onFilterChange: () => {},
  clearFilters: () => {},
  selectableRows: [],
  sortField: '',
  sortDirection: 'none',
  onClickSort: () => {},
});

export function TableContextProvider<T>({
  children,
  onSelectedRowsChange,
  isRowSelectable,
  rows,
}: React.PropsWithChildren<
  Pick<TableProps<T>, 'rows' | 'onSelectedRowsChange' | 'isRowSelectable'>
>) {
  const { filteredRows, filters, onFilterChange, clearFilters } = useFilter(rows);

  const { sortedRows, onClickSort, direction: sortDirection, sortField } = useSort(filteredRows);

  const { onSelectAllRows, allRowsSelected, onSelectRow, selectedRows, selectableRows } = useSelect(
    filteredRows,
    onSelectedRowsChange,
    isRowSelectable,
  );

  const value = useMemo(
    () => ({
      rows,
      selectedRows,
      sortDirection,
      sortField,
      sortedRows,
      filteredRows,
      filters,
      onFilterChange,
      clearFilters,
      selectableRows,
      allRowsSelected,
      onClickSort,
      onSelectRow,
      onSelectAllRows,
    }),
    [
      rows,
      selectableRows,
      selectedRows,
      sortDirection,
      sortField,
      sortedRows,
      filteredRows,
      filters,
      onFilterChange,
      clearFilters,
      allRowsSelected,
      onClickSort,
      onSelectRow,
      onSelectAllRows,
    ],
  );

  return <TableContext.Provider value={value}>{children}</TableContext.Provider>;
}
